import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { ADD_GADGET_EXPERIENCE } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useRightPanelState,
	useRightPanelToggle,
} from '../../../controllers/right-panel-layout-controller.tsx';
import { addGadgetPanelId } from './add-gadget-panel/constants.tsx';
import { ADD_GADGET_BUTTON } from './constants.tsx';
import messages from './messages.tsx';
import type { AddGadgetButtonProps } from './types.tsx';

export const AddGadgetButtonInner = ({ buttonType, columnIndex }: AddGadgetButtonProps) => {
	const { formatMessage } = useIntl();
	const { onOpenRightPanel, onFocusTrigger } = useRightPanelToggle();
	const { isTriggerFocused } = useRightPanelState();

	const buttonRef = useRef<HTMLButtonElement>(null);

	const addGadgetButtonOnClick = useCallback(() => {
		onOpenRightPanel(`${addGadgetPanelId}-${columnIndex}`);
	}, [onOpenRightPanel, columnIndex]);

	useLayoutEffect(() => {
		onFocusTrigger(() => buttonRef.current?.focus?.());
	}, [onFocusTrigger, isTriggerFocused]);

	return (
		<AddGadgetWrapper>
			<Button
				ref={buttonRef}
				appearance={buttonType === ADD_GADGET_BUTTON ? 'default' : 'link'}
				spacing={buttonType === ADD_GADGET_BUTTON ? 'default' : 'none'}
				onClick={addGadgetButtonOnClick}
			>
				{formatMessage(buttonType === ADD_GADGET_BUTTON ? messages.title : messages.linkTitle)}
			</Button>
		</AddGadgetWrapper>
	);
};

export const AddGadgetButton = (props: AddGadgetButtonProps) => (
	<JSErrorBoundary
		id="add-gadget"
		packageName="dashboard"
		fallback="flag"
		attributes={{
			task: ADD_GADGET_EXPERIENCE,
			reason: 'AddGadgetButton with panel ErrorBoundary',
		}}
		withExperienceTracker
	>
		<AddGadgetButtonInner {...props} />
	</JSErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const AddGadgetWrapper = styled.div({
	display: 'inline',
});
